import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AddScooterPage from "@/pages/AddScooterPage";
import StockListPage from "@/pages/StockListPage";
import ScooterDetailPage from "@/pages/ScooterDetailPage";
import EditScooterPage from "@/pages/EditScooterPage";
import PublicScooterPage from "@/pages/PublicScooterPage";
import ShowroomPage from "@/pages/ShowroomPage";
import PromoPage from "@/pages/PromoPage";
import PartsListPage from "@/pages/PartsListPage";
import AddPartPage from "@/pages/AddPartPage";
import PartDetailPage from "@/pages/PartDetailPage";
import EditPartPage from "@/pages/EditPartPage";
import PublicPartPage from "@/pages/PublicPartPage";
import PartsShowroomPage from "@/pages/PartsShowroomPage";
import BoutiquePage from "@/pages/BoutiquePage";
import AdminPage from "@/pages/AdminPage";
import TrackOrderPage from "@/pages/TrackOrderPage";
import CustomerAccountPage from "@/pages/CustomerAccountPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import ReceptionPage from "@/pages/ReceptionPage";
import AuctionsPage from "@/pages/AuctionsPage";
import CategoryDiscountsPage from "@/pages/CategoryDiscountsPage";
import CustomerAuthCallback from "@/pages/CustomerAuthCallback";
import BusinessCardPage from "@/pages/BusinessCardPage";
import BusinessCardQRPage from "@/pages/BusinessCardQRPage";
import EicmaPage from "@/pages/EicmaPage";
import EicmaLeadsPage from "@/pages/EicmaLeadsPage";
import EicmaTranslationsPage from "@/pages/EicmaTranslationsPage";
import AdminAuctionsPage from "@/pages/AdminAuctionsPage";
import PublicQRPage from "@/pages/PublicQRPage";
import LabelsPage from "@/pages/LabelsPage";
import PhotographerPage from "@/pages/PhotographerPage";
import QRScanPage from "@/pages/QRScanPage";
import QuickRegisterPage from "@/pages/QuickRegisterPage";
import ReportsPage from "@/pages/ReportsPage";
import MechanicPartsPage from "@/pages/MechanicPartsPage";
import ProgramariPage from "@/pages/ProgramariPage";
import MechanicDashboardPage from "@/pages/MechanicDashboardPage";
import MechanicFichePage from "@/pages/MechanicFichePage";

const ProtectedRoute = ({ children, allowMechanic = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-mono">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Mechanic role: redirect to mechanic dashboard unless on allowed pages
  if (user.role === "mechanic" && !allowMechanic) {
    const mechanicAllowed = ["/mechanic-dashboard", "/mechanic-fiche", "/mechanic-parts"];
    const isAllowed = mechanicAllowed.some(p => location.pathname.startsWith(p));
    if (!isAllowed) {
      return <Navigate to="/mechanic-dashboard" replace />;
    }
  }

  // Photographer role: redirect to photographer page unless on allowed pages
  if (user.role === "photographer") {
    const photoAllowed = ["/photographer", "/scan"];
    const isAllowed = photoAllowed.some(p => location.pathname.startsWith(p));
    if (!isAllowed) {
      return <Navigate to="/photographer" replace />;
    }
  }
  
  return children;
};

function AppRoutes() {
  const [liveBanner, setLiveBanner] = useState({ active: false, message: "" });

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auction-settings/live-banner`);
        const data = await res.json();
        setLiveBanner(data);
      } catch (e) { /* silent - banner check */ }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  const location = useLocation();
  // Google sign-in returns with #session_id=... - handle it before any route
  if (location.hash?.includes("session_id=")) {
    return <CustomerAuthCallback />;
  }

  return (
    <>
      {/* LIVE Banner - shows on ALL pages when auction is active */}
      {liveBanner.active && (
        <div className="bg-red-600 text-white py-2 px-4 text-center text-sm font-bold animate-pulse sticky top-0 z-[60]" data-testid="live-banner">
          <a href="/auctions" className="flex items-center justify-center gap-2">
            <span className="w-3 h-3 bg-white rounded-full animate-ping inline-block" />
            LICITATIE IN DIRECT — {liveBanner.message || "Votati acum!"} 
            <span className="underline ml-2">Intra acum →</span>
          </a>
        </div>
      )}
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public routes */}
      <Route path="/" element={<BoutiquePage />} />
      <Route path="/boutique" element={<BoutiquePage />} />
      <Route path="/scooter/:scooterId" element={<PublicScooterPage />} />
      <Route path="/showroom" element={<ShowroomPage />} />
      <Route path="/part/:partId" element={<PublicPartPage />} />
      <Route path="/parts-showroom" element={<PartsShowroomPage />} />
      <Route path="/promo" element={<PromoPage />} />
      <Route path="/track" element={<TrackOrderPage />} />
      <Route path="/track/:trackingCode" element={<TrackOrderPage />} />
      <Route path="/account" element={<CustomerAccountPage />} />
      {/* Licitatii hidden from public for now */}
      {["/auctions", "/encheres", "/licitatii", "/live", "/auctions/:auctionId", "/live-auction"].map(p => (
        <Route key={p} path={p} element={<Navigate to="/" replace />} />
      ))}
      <Route path="/qr/:scooterId" element={<PublicQRPage />} />
      <Route path="/carte" element={<BusinessCardPage />} />
      <Route path="/eicma/:category" element={<EicmaPage />} />
      <Route path="/eicma-leads" element={<ProtectedRoute><EicmaLeadsPage /></ProtectedRoute>} />
      <Route path="/eicma-traduceri" element={<ProtectedRoute><EicmaTranslationsPage /></ProtectedRoute>} />
      <Route path="/carte-qr" element={<ProtectedRoute><BusinessCardQRPage /></ProtectedRoute>} />
      <Route path="/reduceri" element={<ProtectedRoute><CategoryDiscountsPage /></ProtectedRoute>} />
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-scooter"
        element={
          <ProtectedRoute>
            <AddScooterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock"
        element={
          <ProtectedRoute>
            <StockListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/:scooterId"
        element={
          <ProtectedRoute>
            <ScooterDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/:scooterId/edit"
        element={
          <ProtectedRoute>
            <EditScooterPage />
          </ProtectedRoute>
        }
      />
      {/* Parts routes */}
      <Route
        path="/parts"
        element={
          <ProtectedRoute>
            <PartsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts/add"
        element={
          <ProtectedRoute>
            <AddPartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts/:partId"
        element={
          <ProtectedRoute>
            <PartDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts/:partId/edit"
        element={
          <ProtectedRoute>
            <EditPartPage />
          </ProtectedRoute>
        }
      />
      {/* Clients routes */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId"
        element={
          <ProtectedRoute>
            <ClientDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Reception routes */}
      <Route
        path="/reception"
        element={
          <ProtectedRoute>
            <ReceptionPage />
          </ProtectedRoute>
        }
      />
      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/auctions"
        element={
          <ProtectedRoute>
            <AdminAuctionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/labels"
        element={
          <ProtectedRoute>
            <LabelsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/photographer"
        element={
          <ProtectedRoute>
            <PhotographerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan"
        element={
          <ProtectedRoute>
            <QRScanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/register-scooter"
        element={
          <ProtectedRoute>
            <QuickRegisterPage />
          </ProtectedRoute>
        }
      />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/mechanic-parts" element={<ProtectedRoute allowMechanic><MechanicPartsPage /></ProtectedRoute>} />
      <Route path="/programari" element={<ProtectedRoute><ProgramariPage /></ProtectedRoute>} />
      <Route path="/mechanic-dashboard" element={<ProtectedRoute allowMechanic><MechanicDashboardPage /></ProtectedRoute>} />
      <Route path="/mechanic-fiche/:scooterId" element={<ProtectedRoute allowMechanic><MechanicFichePage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <div className="noise-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
